export type { UserRole, RoleSettings } from './shared';
import type { UserRole, RoleSettings } from './shared';
import type { LucideIcon } from 'lucide-react';

export type NavItem = {
  id: string;
  label: string;
  icon: string | LucideIcon;
  path: string;
  children?: NavItem[];
  href?: string;
  requiredRole?: UserRole;
  badge?: string | number;
};

export type LinkNavItem = NavItem & {
  href: string;
};

export type ActionNavItem = NavItem & {
  action: () => void;
};

export type SidebarState = {
  isOpen: boolean;
  isCollapsed: boolean;
  activeItemId: string;
};

// Navigation configuration type
export type NavigationConfig = Record<UserRole, NavItem[]>;

// Type-safe navigation map
export type NavigationMap = {
  [Role in UserRole]: NavItem[];
};

// Route configuration for type-safe routing
export type RouteConfig = {
  path: string;
  requiredRole: UserRole;
  label: string;
};

// Comprehensive route definitions
export const APP_ROUTES: Record<string, RouteConfig> = {
  LOGIN: {
    path: '/login',
    requiredRole: 'guest',
    label: 'Login'
  },
  DASHBOARD: {
    path: '/dashboard',
    requiredRole: 'agent',
    label: 'Dashboard'
  },
  PROFILE: {
    path: '/profile',
    requiredRole: 'agent',
    label: 'Profile'
  },
  NEW_SALE: {
    path: '/sales/new',
    requiredRole: 'agent',
    label: 'New Sale'
  },
  TEAM_PERFORMANCE: {
    path: '/team/performance',
    requiredRole: 'upline',
    label: 'Team Performance'
  },
  AGENT_MANAGEMENT: {
    path: '/agency/agents',
    requiredRole: 'admin',
    label: 'Agent Management'
  },
  AGENCY_SETTINGS: {
    path: '/agency/settings',
    requiredRole: 'admin',
    label: 'Agency Settings'
  },
  REPORTS: {
    path: '/reports',
    requiredRole: 'manager',
    label: 'Reports'
  }
};

// Type guard for navigation items
export function isNavItem(item: unknown): item is NavItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    'label' in item &&
    'path' in item
  );
}

// Type guard for link navigation items
export function isLinkNavItem(item: unknown): item is LinkNavItem {
  return isNavItem(item) && 'href' in item;
}

// Type guard for action navigation items
export function isActionNavItem(item: unknown): item is ActionNavItem {
  return isNavItem(item) && 'action' in item;
}
